import { ReactElement, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography as T,
} from "@mui/material";
import pluralize from "pluralize";
import EnvironmentActions from "rwb/components/EnvironmentActions";
import { ProjectsResponse } from "rwb/hooks/useProjects";
import EnvironmentStatus from "rwb/pages/project/types/enums/environmentStatus";
import { ComputeEnvironment } from "rwb/types/interfaces/project";
import { KeyedMutator } from "swr";
import { PlusIcon, SettingsToolIcon } from "~/assets/icons";
import StatusIndicator from "~/components/StatusIndicator";
import TextOverflow from "~/components/TextOverflow";

interface EnvironmentsStatusProps {
  projectId: number;
  environments: ComputeEnvironment[] | null;
  mutateProjects: KeyedMutator<ProjectsResponse>;
}

const EnvironmentsTooltip = ({
  projectId,
  environments = [],
  mutateProjects,
  children,
}: EnvironmentsStatusProps & { children: ReactElement }) => {
  const [error, setError] = useState("");

  return (
    <Tooltip
      arrow
      variant="light"
      // Prevent click on tooltip from propagating to card and navigating user
      slotProps={{
        popper: {
          onClick: (e) => e.stopPropagation(),
          // Hide tooltip when overlaid stop env modal adds aria-hidden to popper
          sx: { '&[aria-hidden="true"]': { display: "none" } },
        },
      }}
      title={
        <Box p={1} width={250}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <T variant="subtitle2">Compute Environments</T>
            <IconButton size="small" component={Link} to={`/workbenches/${projectId}/environments`}>
              <SettingsToolIcon width={18} fill="#9e9e9e" color="#9e9e9e" />
            </IconButton>
          </Box>
          <Divider sx={{ my: 1 }} />
          {error && (
            <Alert severity="error" sx={{ my: 1 }}>
              {error}
            </Alert>
          )}
          <Stack gap={1} py={1}>
            {environments?.map((env) => (
              <Box key={env.id} display="flex" alignItems="center" gap={1}>
                <StatusIndicator active={env.status === EnvironmentStatus.RUNNING} />
                <TextOverflow variant="body2" flex={1}>
                  {env.name}
                </TextOverflow>
                <EnvironmentActions env={env} setError={setError} refreshEnvs={mutateProjects} />
              </Box>
            ))}
          </Stack>
        </Box>
      }
    >
      {children}
    </Tooltip>
  );
};

const EnvironmentsStatus = ({
  projectId,
  environments = [],
  mutateProjects,
}: EnvironmentsStatusProps) => {
  const navigate = useNavigate();
  const environmentCount = environments?.length ?? 0;

  if (!environmentCount) {
    return (
      <Button
        size="small"
        sx={{ ml: -1, fontSize: 12, color: "primary.main" }}
        onClick={(e) => {
          e.preventDefault();
          navigate(`/workbenches/${projectId}/environments`, {
            state: { createNew: !environmentCount },
          });
        }}
      >
        <Box pr={1}>
          <PlusIcon width={10} />
        </Box>
        Add environment
      </Button>
    );
  }

  return (
    <EnvironmentsTooltip
      environments={environments}
      projectId={projectId}
      mutateProjects={mutateProjects}
    >
      <Stack direction="row" gap={1} alignItems="center" minWidth={0}>
        <StatusIndicator
          active={environments?.some((e) => e.status === EnvironmentStatus.RUNNING) ?? false}
        />
        <TextOverflow variant="body2" fontSize={12}>
          {environments?.length} {pluralize("environment", environments?.length)}
        </TextOverflow>
      </Stack>
    </EnvironmentsTooltip>
  );
};

export { EnvironmentsStatus };
